import type { ButtonOverrides } from 'baseui/button';
import { Button, KIND } from 'baseui/button';
import { ChevronLeft, ChevronRight } from 'baseui/icon';
import { StyledTable } from 'baseui/table-grid';
import React, { useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../../globalStyles';
import type { SortDirection } from '../../Hooks/useList/types';
import { MantraSpinner } from '../LoadingOverlay';
import { SortIndicator } from './SortIndicator';
import { BodyCell, HeadCell, LinkCell } from './styles';
import type { DataTableProps } from './types';

/**
 * Table-only renderer of the `DataTableProps` contract. It owns no state and does no
 * fetching — it renders exactly the rows/columns/pagination it is given, usually mapped from
 * `useListPagination`. Search and filters are the consumer's toolbar, composed around this.
 * Swapping to another table library is a sibling component with the same props.
 */
export const DataTable = <T,>({
  rows,
  columns,
  getRowKey,
  loading,
  empty = 'None',
  sort,
  onToggleSort,
  pageInfo,
  onNextPage,
  onPrevPage,
  onRowClick,
  rowHref,
  isRowDisabled,
  fontSize,
}: DataTableProps<T> & {
  fontSize?: 'f4' | 'f5' | 'f6';
}): React.ReactElement => {
  const visibleColumns = columns.filter(column => !column.hidden);

  const rangeLabel = useMemo(
    () =>
      rows.length === 0
        ? 'Viewing 0-0'
        : `Viewing ${pageInfo.from}-${pageInfo.to}${
            pageInfo.count != null
              ? ` of ${pageInfo.count}${pageInfo.countExceeded ? '+' : ''}`
              : ''
          }`,
    [rows.length, pageInfo]
  );

  // After Next/Prev, focus moves to the new page's first row once it renders, so keyboard and
  // screen reader users land on the rows they asked for. The rows on screen when the pager was
  // pressed tell the new page apart from the old one.
  const firstCellRef = useRef<HTMLDivElement>(null);
  const rowsWhenPaged = useRef<T[] | null>(null);

  useEffect(() => {
    const pagedFrom = rowsWhenPaged.current;
    if (pagedFrom === null || loading || rows === pagedFrom) return;
    rowsWhenPaged.current = null;
    const cell = firstCellRef.current;
    // A link row's entry point is the link wrapping its first cell.
    const entry = cell?.parentElement instanceof HTMLAnchorElement ? cell.parentElement : cell;
    entry?.focus();
  }, [loading, rows]);

  const turnPage = (onTurn: () => void) => () => {
    rowsWhenPaged.current = rows;
    onTurn();
  };

  return (
    <div>
      <StyledTable
        $gridTemplateColumns={visibleColumns.map(column => column.width || '1fr').join(' ')}
      >
        {visibleColumns.map(column => {
          const activeDirection = sort && sort.field === column.key ? sort.direction : false;
          if (!column.sortable) {
            return (
              <HeadCell key={column.key} style={{ justifyContent: alignToJustify(column.align) }}>
                {column.title}
              </HeadCell>
            );
          }
          return (
            <HeadCell key={column.key}>
              <SortButton
                type="button"
                onClick={() => onToggleSort(column.key)}
                aria-label={sortLabel(column.title, activeDirection)}
                style={{ justifyContent: alignToJustify(column.align) }}
              >
                <SortIndicator direction={activeDirection}>{column.title}</SortIndicator>
              </SortButton>
            </HeadCell>
          );
        })}
        {rows.map((row, index) => {
          const rowKey = getRowKey(row);
          const disabled = isRowDisabled?.(row) ?? false;
          const href = disabled ? undefined : rowHref?.(row);
          const activate = onRowClick && !disabled ? () => onRowClick(row) : undefined;
          // A link row is already keyboard-reachable through its anchors; a click-only row is
          // not, so its first cell becomes the row's button (one tab stop per row).
          const isButtonRow = activate != null && href == null;
          return (
            <React.Fragment key={rowKey}>
              {visibleColumns.map((column, columnIndex) => {
                const cellKey = `${column.key}-${rowKey}`;
                const isRowButton = isButtonRow && columnIndex === 0;
                const isFirstCell = index === 0 && columnIndex === 0;
                const cell = (
                  <RowCell
                    ref={isFirstCell ? firstCellRef : undefined}
                    onClick={activate}
                    role={isRowButton ? 'button' : undefined}
                    // A first row with no link or button still takes focus after paging, without
                    // joining the tab order.
                    tabIndex={isRowButton ? 0 : isFirstCell && href == null ? -1 : undefined}
                    onKeyDown={isRowButton && activate ? activateOnKey(activate) : undefined}
                    $disabled={disabled}
                    className={fontSize}
                    data-cy={`row_${index}`}
                    style={{ justifyContent: alignToJustify(column.align) }}
                  >
                    {column.render(row)}
                  </RowCell>
                );
                // One tab stop per link row: its first cell. The other cells stay links for the
                // mouse (cmd-click opens a tab) but leave the tab order.
                return href != null ? (
                  <RowLinkCell key={cellKey} to={href} tabIndex={columnIndex === 0 ? 0 : -1}>
                    {cell}
                  </RowLinkCell>
                ) : (
                  <React.Fragment key={cellKey}>{cell}</React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}
        {loading && (
          <div className="flex justify-center pa3" style={{ gridColumn: '1 / -1' }}>
            <MantraSpinner size={24} simple />
          </div>
        )}
        {rows.length === 0 && !loading && (
          <BodyCell style={{ gridColumn: '1 / -1' }}>{empty}</BodyCell>
        )}
      </StyledTable>
      {/* Prev/next only — sequential pagination, no jump-to-page (see useListPagination). */}
      <div className="mt3 flex items-center">
        <Button
          type="button"
          kind={KIND.tertiary}
          onClick={pageInfo.hasPrev ? turnPage(onPrevPage) : undefined}
          overrides={pagerButtonOverrides(pageInfo.hasPrev)}
          startEnhancer={<ChevronLeft size={24} />}
        >
          Prev
        </Button>
        {/* Read through the status region below, which also announces loads. */}
        <div className="mh3" aria-hidden="true">
          {rangeLabel}
        </div>
        <StatusText role="status">{loading ? 'Loading' : rangeLabel}</StatusText>
        <Button
          type="button"
          kind={KIND.tertiary}
          onClick={pageInfo.hasNext ? turnPage(onNextPage) : undefined}
          overrides={pagerButtonOverrides(pageInfo.hasNext)}
          endEnhancer={<ChevronRight size={24} />}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

/**
 * Sortable headers render a real <button> so sorting is keyboard-operable (focusable,
 * Enter/Space), since HeadCell is a plain div. The button resets native chrome to inherit the
 * HeadCell's typography. The grid has no table/columnheader roles, so `aria-sort` would be
 * ignored; the sort state goes in the button's accessible name instead.
 */
const SortButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
`;

/** Visible keyboard focus on row entry points — `LinkCell` strips the outline. */
const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: -2px;
  }
`;

const RowCell = styled(BodyCell)`
  ${focusRing}
`;

const RowLinkCell = styled(LinkCell)`
  ${focusRing}
`;

/**
 * Enter/Space activate a row button once per press — a held key's auto-repeat is ignored. Keys
 * pressed inside a nested control (input, checkbox) are left alone.
 */
const activateOnKey =
  (activate: () => void) =>
  (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault(); // Space would otherwise scroll the page
    if (event.repeat) return;
    activate();
  };

/** The sort button's accessible name — the title, plus the direction when it is the active sort. */
const sortLabel = (title: string, direction: SortDirection | false): string =>
  direction ? `${title}, sorted ${direction === 'asc' ? 'ascending' : 'descending'}` : title;

/**
 * Pager buttons are never natively disabled: `Next` reads as unavailable while a page loads, and
 * disabling the focused button would drop keyboard focus to the document on every page turn.
 * `aria-disabled` announces the state, `$disabled` drops the kind's hover colors, and the style
 * mirrors baseui's own `:disabled` look; the click handler is withheld.
 */
const pagerButtonOverrides = (enabled: boolean): ButtonOverrides => ({
  BaseButton: {
    props: { $disabled: !enabled, 'aria-disabled': !enabled },
    style: ({ $theme }) =>
      enabled
        ? {}
        : {
            cursor: 'not-allowed',
            backgroundColor: $theme.colors.buttonDisabledFill,
            color: $theme.colors.buttonDisabledText,
          },
  },
});

/** Visually hidden, still read by screen readers. */
const StatusText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

/** A column's `align` as a flexbox `justify-content` (cells are flex rows); left → default. */
const alignToJustify = (align?: 'left' | 'center' | 'right'): 'center' | 'flex-end' | undefined =>
  align === 'center' ? 'center' : align === 'right' ? 'flex-end' : undefined;
